import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { FlagsProvider } from '@atlaskit/flag';
import { Inline, Text } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import { ConfluenceLabsLozenge } from '@confluence/ai-common/entry-points/ConfluenceLabsLozenge';
import { SmartsFeedbackComponent } from '@confluence/quick-summary/entry-points/SmartsFeedbackComponent';
import { FeedbackCollectorPropertiesProvider } from '@confluence/quick-summary/entry-points/FeedbackCollectorPropertiesProvider';

import type { ActiveAudioContent } from '../../types/activeAudioContent';
import type { AudioFeatureType } from '../../types/audioFeatureType';

const ANALYTICS_SOURCE = 'aiAudio';
const AUDIO_FEEDBACK_ENTRYPOINT_ID = 'ffb1f355-3d3c-45c7-a0d1-1f13eb13baa3';

const i18n = defineMessages({
	qualityDisclaimer: {
		id: 'audio.audio-popup.feedback-footer.quality-disclaimer',
		defaultMessage: 'Uses AI. Verify results.',
		description:
			'A disclaimer in the footer of our audio player warning that AI generated results may vary.',
	},
});

export type AudioPopupFooterProps = {
	activeContent: ActiveAudioContent | undefined;
	activeFeature: AudioFeatureType | undefined;
};

const styles = cssMap({
	footerWrapper: {
		paddingBlock: token('space.100'),
		paddingInline: token('space.200'),
		borderTopWidth: token('border.width'),
		borderTopColor: token('color.border'),
		borderTopStyle: 'solid',
	},
});

export const AudioPopupFooterComponent = ({
	activeContent,
	activeFeature,
}: AudioPopupFooterProps) => {
	const { formatMessage } = useIntl();

	return (
		<Inline spread="space-between" alignBlock="center" grow="fill" xcss={styles.footerWrapper}>
			<Inline alignBlock="center" space="space.100">
				<ConfluenceLabsLozenge />
				<Text size="small" color="color.text.subtlest">
					{formatMessage(i18n.qualityDisclaimer)}
				</Text>
			</Inline>
			<SmartsFeedbackComponent
				contentId={activeContent?.id ?? ''}
				analyticsProperties={{
					source: ANALYTICS_SOURCE,
					additionalAnalyticsAttributes: {
						contentType: activeContent?.contentType ?? '',
						feature: activeFeature,
					},
				}}
			/>
		</Inline>
	);
};

export const AudioPopupFooter = ({ activeContent, activeFeature }: AudioPopupFooterProps) => {
	return (
		<FlagsProvider>
			<FeedbackCollectorPropertiesProvider
				entrypointId={AUDIO_FEEDBACK_ENTRYPOINT_ID}
				feedbackContext={{
					feature: activeFeature,
				}}
			>
				<AudioPopupFooterComponent activeContent={activeContent} activeFeature={activeFeature} />
			</FeedbackCollectorPropertiesProvider>
		</FlagsProvider>
	);
};
